// frontend/src/components/checklists/RelatedChecklistsByReviewIdMenu.tsx
import { Badge, Button, Group, Loader, Menu, Paper, Stack, Text, ThemeIcon } from "@mantine/core";
import React from "react";
import { useHistory } from "react-router-dom";
import { useGetChecklistsByReviewByReviewIdQuery } from "../../../../../openapi";
import { selectCurrentProjectId } from "../../../../global/globalSliceCommon";
import { useAppSelector } from "../../../../hooks";
import {
  makeSelectRelatedChecklistDkStats,
  selectHasDkApprovalEnabledForCurrentChecklist,
} from "./relatedChecklistsSelectors";

export const RelatedChecklistsByReviewIdMenu: React.FC = () => {
  const projectId = useAppSelector(selectCurrentProjectId);
  const checklistId = useAppSelector((state) => state.checklistReducer.checklist?.id);
  const reviewId = useAppSelector((state) => state.checklistReducer.checklist?.doksam?.reviewId);
  const hasDkApprovalEnabled = useAppSelector(selectHasDkApprovalEnabledForCurrentChecklist);
  const trimmedReviewId = (reviewId ?? "").trim();
  const selectRelatedChecklistDkStats = React.useMemo(
    () => makeSelectRelatedChecklistDkStats(trimmedReviewId),
    [trimmedReviewId],
  );
  const { dkApprovedCount } = useAppSelector(selectRelatedChecklistDkStats);

  const {
    data = [],
    isLoading,
    isFetching,
    isError,
  } = useGetChecklistsByReviewByReviewIdQuery({
    reviewId: trimmedReviewId,
  });

  const busy = isLoading || isFetching;

  const history = useHistory();

  const handlePick = (_id: string) => {
    history.push(`/project/${projectId}/checklist/instance/${_id}?doksam=true`);
  };

  if (!reviewId) {
    return (
      <Stack gap={6}>
        <Text size="sm" c="dimmed">
          Ingen reviewId – kan ikke hente relaterte checklister.
        </Text>
        <Badge color="gray" leftSection={<i className="fa fa-info-circle" />}>
          Tom reviewId
        </Badge>
      </Stack>
    );
  }

  if (data.length === 0 && !busy && !isError) {
    return (
      <Text size="sm" c="dimmed">
        Ingen relaterte checklister funnet.
      </Text>
    );
  }

  return (
    <Group justify="space-between" align="center">
      <Stack gap="xs">
        {busy && <Loader size="xs" />}
        {isError && (
          <Badge color="red" leftSection={<i className="fa fa-times" />}>
            Feil
          </Badge>
        )}

        {/* Ekstern label for bedre synlighet */}
        {data.length > 1 && (
          <Paper
            p="xs"
            radius="md"
            withBorder
            bg="mc-primary.9"
            style={{
              borderColor: "var(--mantine-color-mc-primary-4)",
              boxShadow: "0 0 0 1px rgba(255,255,255,0.16), 0 8px 22px rgba(0,0,0,0.22)",
            }}
          >
            <Stack gap={6}>
              <Group gap="xs" align="center">
                <ThemeIcon color="mc-primary" variant="white" size="sm" radius="xl">
                  <i className="fa fa-link" />
                </ThemeIcon>
                <Text size="sm" tt="uppercase" fw={800} c="white">
                  Relaterte sjekklister
                </Text>
                <Badge color="mc-primary" variant="white" size="lg">
                  {data.length}
                </Badge>
              </Group>

              <Text size="md" fw={700} c="white">
                Sjekklister med samme kontrollflyt
              </Text>

              {hasDkApprovalEnabled && (
                <Group gap="xs" align="center">
                  <Badge color={dkApprovedCount > 0 ? "green" : "gray"} variant="light">
                    DK-godkjent {dkApprovedCount}/{data.length}
                  </Badge>
                </Group>
              )}

              {/* Visa endast dropdownen om det finns mer än 1 item i data */}
              <Menu shadow="xl" width={420} position="bottom-end" withinPortal>
                <Menu.Target>
                  <Button
                    variant="filled"
                    color="mc-primary"
                    size="sm"
                    leftSection={<i className="fa fa-list" />}
                    style={{ boxShadow: "0 4px 14px rgba(116, 164, 214, 0.45)" }}
                  >
                    Velg sjekkliste
                  </Button>
                </Menu.Target>

                <Menu.Dropdown>
                  {data.map((it) => (
                    <Menu.Item
                      key={it.checklistNrId}
                      onClick={() => handlePick(it._id)}
                      leftSection={
                        checklistId === it.checklistNrId ? <i className="fa fa-check" /> : <i className="fa fa-file" />
                      }
                      rightSection={
                        it.doksam?.dkApproval?.approvedAt ? (
                          <i className="fa fa-check-circle" style={{ color: "var(--mantine-color-green-6)" }} />
                        ) : undefined
                      }
                    >
                      {it.checklistNrId} - {it.name}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Group>
  );
};

export default RelatedChecklistsByReviewIdMenu;
